import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, typography, radius } from '@/shared/theme/tokens';

/**
 * FormField — label + input slot + optional error message.
 * Replaces the 20+ identical FormField patterns across page files.
 *
 * Props:
 *   label     — field label string
 *   required  — show asterisk (default false)
 *   error     — error string; shows error state when truthy
 *   children  — the actual input/select/textarea element
 *   hint      — optional helper text below input (optional)
 */
export function FormField({ label, required = false, error, hint, children }) {
  const c = useThemeColors();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && (
        <label style={{
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.semibold,
          color: c.textLabel,
          letterSpacing: typography.letterSpacing.label,
          textTransform: 'uppercase',
          fontFamily: typography.fontFamily.body,
        }}>
          {label}
          {required && <span style={{ color: semantic.error, marginLeft: '3px' }}>*</span>}
        </label>
      )}

      {children}

      {error && (
        <p style={{
          margin: 0,
          fontSize: typography.fontSize.sm,
          color: semantic.error,
          fontWeight: typography.fontWeight.medium,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 13, verticalAlign: 'middle' }}>error</span>
          {error}
        </p>
      )}

      {hint && !error && (
        <p style={{
          margin: 0,
          fontSize: typography.fontSize.sm,
          color: c.textSubtle,
        }}>
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * fieldInputStyle — returns consistent input style object.
 * Use on <input>, <select>, <textarea> inside FormField.
 *
 * Usage:
 *   const c = useThemeColors();
 *   <input style={fieldInputStyle(c, hasError)} />
 */
export function fieldInputStyle(c, hasError = false) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    background: c.bgSubtle,
    border: `1.5px solid ${hasError ? semantic.error : c.borderInput}`,
    borderRadius: radius.lg,
    padding: '12px 14px',
    minHeight: '44px',
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.body,
    color: c.textPrimary,
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxShadow: hasError ? `0 0 0 3px ${semantic.errorContainer}` : 'none',
  };
}
